import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { BaseError, BaseResponse } from '@utils/types/shared/base.ts';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

export interface CustomerBoIAM {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  newsletter_consent?: boolean;
  roles?: ['admin' | 'contributor' | 'reseller'] | [];
}

export type CustomerBoIAMResponse = BaseResponse<CustomerBoIAM[]>;

export const useGetCustomerBoIAM = (customerId: number) => {
  const { t } = useTranslation();
  const { searchParams } = useNewAppSearchParams();

  const offset = Number.isFinite(Number(searchParams.offset))
    ? Number(searchParams.offset)
    : 0;
  const limit = Number.isFinite(Number(searchParams.limit))
    ? Number(searchParams.limit)
    : 25;

  const sortField = (searchParams.sort as string | undefined) || undefined;
  const sortDirection =
    (searchParams.direction as 'asc' | 'desc' | undefined) || undefined;

  const qString = (searchParams.q as string | undefined) || undefined;

  // Valid sort fields for user endpoint - filter out invalid ones like 'status'
  const validSortFields = [
    'email',
    'first_name',
    'last_name',
    'id',
    'created_at'
  ];
  const safeSortField =
    sortField && validSortFields.includes(sortField) ? sortField : undefined;

  const fetchIam = async () => {
    const { customerBoIamByCustomerId } = GET_URL_NEW({
      offset,
      limit,
      q: qString,
      sort: safeSortField,
      direction: safeSortField ? sortDirection : undefined
    });

    const url = customerBoIamByCustomerId(customerId);
    const { data } = await Axios.get<CustomerBoIAMResponse>(url);
    return data;
  };

  return useQuery<CustomerBoIAMResponse, AxiosError<BaseError>>(
    [
      'customerBoIam',
      customerId,
      offset,
      limit,
      qString,
      safeSortField,
      safeSortField ? sortDirection : undefined
    ],
    fetchIam,
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
      onError: error => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const d = error.response?.data as any;
        const msg =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          t('an-error-has-occurred');
        toast.error(String(msg));
      }
    }
  );
};
